import { ensureCustomFilterStyle } from './filterStyles';

/**
 * PriceRangeFilter
 *
 * 가격 범위 필터 (최고가, 전고가, 적고가, 전저가 등)
 * 슬라이더와 입력 필드로 범위 조절 가능
 */
class PriceRangeFilter {
  constructor() {
    this.model = null; // { min: 10000, max: 50000 }
    this.eGui = null;
    this.params = null;
  }

  init(params) {
    ensureCustomFilterStyle();
    this.params = params;
    this.onClose = params.onClose || null;
    this.eGui = document.createElement('div');
    this.eGui.className = 'aptrank-custom-filter';
    this.render();
  }

  getGui() {
    return this.eGui;
  }

  render() {
    const from = this.model?.min ?? '';
    const to = this.model?.max ?? '';
    // 설정값이 없으면 슬라이더는 전체 범위(min=좌측끝, max=우측끝)로 배치해 두 핸들이 겹치지 않도록.
    // Number('')는 0(유한값)이라 단순 isFinite 검사로는 빈 값 판별이 안 됨 → 빈 문자열 명시 처리.
    const sliderMin = from === '' ? 0 : Math.max(0, Math.min(1000000, Number(from) || 0));
    const sliderMax = to === '' ? 1000000 : Math.max(0, Math.min(1000000, Number(to) || 1000000));

    const formatKoreanPrice = (value) => {
      if (!value) return '';
      const num = Number(value);
      if (!Number.isFinite(num)) return '';
      const billion = Math.floor(num / 10000);
      const million = Math.floor((num % 10000) / 1000);
      if (billion > 0 && million > 0) {
        return `${billion}억 ${million * 1000}만원`;
      } else if (billion > 0) {
        return `${billion}억원`;
      } else {
        return `${num}만원`;
      }
    };

    this.eGui.innerHTML = `
      <button type="button" class="complex-top-filter-panel-close" style="position: absolute; top: 8px; right: 8px; border: none; background: transparent; color: #64748b; width: 28px; height: 28px; border-radius: 8px; font-size: 24px; line-height: 1; cursor: pointer; z-index: 10;">×</button>
      <div class="aptrank-custom-filter-head">
        <div class="aptrank-custom-filter-heading">금액 범위 (만원)</div>
      </div>
      <div class="aptrank-custom-filter-title-edit" style="align-items: flex-start;">
        <div style="display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;">
          <div class="korean-price-label min-korean" style="font-size: 10px; color: #64748b; height: 14px; line-height: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${formatKoreanPrice(from)}</div>
          <input class="title-min" inputmode="numeric" placeholder="최소" value="${from}" />
        </div>
        <span style="margin-top: 16px; flex-shrink: 0;">~</span>
        <div style="display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;">
          <div class="korean-price-label max-korean" style="font-size: 10px; color: #64748b; height: 14px; line-height: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${formatKoreanPrice(to)}</div>
          <input class="title-max" inputmode="numeric" placeholder="최대" value="${to}" />
        </div>
      </div>
      <div class="aptrank-custom-filter-slider-wrap">
        <div class="aptrank-custom-filter-slider-track"></div>
        <input class="aptrank-custom-filter-slider min-slider" type="range" min="0" max="1000000" step="1000" value="${Math.max(0, Math.min(1000000, sliderMin))}" />
        <input class="aptrank-custom-filter-slider max-slider" type="range" min="0" max="1000000" step="1000" value="${Math.max(0, Math.min(1000000, sliderMax))}" />
      </div>
      <div class="aptrank-custom-filter-grid">
        <button class="aptrank-custom-filter-chip" data-range="0-10000">~1억</button>
        <button class="aptrank-custom-filter-chip" data-range="0-20000">~2억</button>
        <button class="aptrank-custom-filter-chip" data-range="0-30000">~3억</button>
        <button class="aptrank-custom-filter-chip" data-range="0-40000">~4억</button>
        <button class="aptrank-custom-filter-chip" data-range="0-50000">~5억</button>
        <button class="aptrank-custom-filter-chip" data-range="50000-100000">5~10억</button>
        <button class="aptrank-custom-filter-chip" data-range="1000000-999999999">100억~</button>
      </div>
      <div class="aptrank-custom-filter-actions">
        <button class="aptrank-custom-filter-reset" type="button">초기화</button>
        <button class="aptrank-custom-filter-apply" type="button">적용</button>
      </div>
    `;

    this.attachEventListeners();
  }

  attachEventListeners() {
    const formatKoreanPrice = (value) => {
      if (!value) return '';
      const num = Number(value);
      if (!Number.isFinite(num)) return '';
      const billion = Math.floor(num / 10000);
      const million = Math.floor((num % 10000) / 1000);
      if (billion > 0 && million > 0) {
        return `${billion}억 ${million * 1000}만원`;
      } else if (billion > 0) {
        return `${billion}억원`;
      } else {
        return `${num}만원`;
      }
    };

    const applyFilter = () => {
      const minRaw = this.eGui.querySelector('input.title-min').value.replace(/\D/g, '');
      const maxRaw = this.eGui.querySelector('input.title-max').value.replace(/\D/g, '');
      if (!minRaw && !maxRaw) {
        this.model = null;
      } else {
        const min = minRaw ? Number(minRaw) : null;
        const max = maxRaw ? Number(maxRaw) : null;
        this.model = { min, max };
      }
      this.params.filterChangedCallback();
    };

    const updateKoreanLabels = () => {
      const minInput = this.eGui.querySelector('input.title-min');
      const maxInput = this.eGui.querySelector('input.title-max');
      const minKorean = this.eGui.querySelector('.min-korean');
      const maxKorean = this.eGui.querySelector('.max-korean');

      if (minKorean) {
        minKorean.textContent = formatKoreanPrice(minInput.value);
      }
      if (maxKorean) {
        maxKorean.textContent = formatKoreanPrice(maxInput.value);
      }
    };

    const updateSlidersFromInputs = () => {
      const minInput = this.eGui.querySelector('input.title-min');
      const maxInput = this.eGui.querySelector('input.title-max');
      const minSlider = this.eGui.querySelector('input.min-slider');
      const maxSlider = this.eGui.querySelector('input.max-slider');

      const minVal = minInput.value.replace(/\D/g, '');
      const maxVal = maxInput.value.replace(/\D/g, '');

      if (minVal) {
        const minNum = Math.min(Number(minVal), 1000000);
        minSlider.value = minNum;
      }
      if (maxVal) {
        const maxNum = Math.min(Number(maxVal), 1000000);
        maxSlider.value = maxNum;
      }
    };

    const updateTrack = () => {
      const minSlider = this.eGui.querySelector('input.min-slider');
      const maxSlider = this.eGui.querySelector('input.max-slider');
      const track = this.eGui.querySelector('.aptrank-custom-filter-slider-track');
      if (!track || !minSlider || !maxSlider) return;

      const minVal = Number(minSlider.value);
      const maxVal = Number(maxSlider.value);
      const leftPercent = (minVal / 1000000) * 100;
      const rightPercent = 100 - (maxVal / 1000000) * 100;

      track.style.left = `${leftPercent}%`;
      track.style.right = `${rightPercent}%`;
    };

    // 슬라이더 변경 시 입력 필드 및 트랙 업데이트
    this.eGui.querySelector('input.min-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const maxText = this.eGui.querySelector('input.title-max').value.replace(/\D/g, '');
      const max = maxText ? Number(maxText) : 1000000;
      this.eGui.querySelector('input.title-min').value = String(Math.min(next, max));
      updateKoreanLabels();
      updateTrack();
    });

    this.eGui.querySelector('input.max-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const minText = this.eGui.querySelector('input.title-min').value.replace(/\D/g, '');
      const min = minText ? Number(minText) : 0;
      this.eGui.querySelector('input.title-max').value = String(Math.max(next, min));
      updateKoreanLabels();
      updateTrack();
    });

    // 입력 필드 변경 시 슬라이더 및 트랙 업데이트
    this.eGui.querySelector('input.title-min').addEventListener('input', () => {
      updateSlidersFromInputs();
      updateKoreanLabels();
      updateTrack();
    });

    this.eGui.querySelector('input.title-max').addEventListener('input', () => {
      updateSlidersFromInputs();
      updateKoreanLabels();
      updateTrack();
    });

    // 초기 트랙 위치 설정
    updateTrack();

    // 빠른 선택 칩 클릭 시 값만 설정 (적용 버튼 눌러야 적용됨)
    this.eGui.querySelectorAll('[data-range]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        const [minStr, maxStr] = String(btn.getAttribute('data-range') || '').split('-');
        const min = Number(minStr || 0);
        const max = Number(maxStr || 999999999);
        this.eGui.querySelector('input.title-min').value = min;
        this.eGui.querySelector('input.title-max').value = max;
        updateSlidersFromInputs();
        updateKoreanLabels();
        updateTrack();
      });
    });

    // 적용 버튼
    this.eGui.querySelector('.aptrank-custom-filter-apply').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      applyFilter();
    });

    // 초기화 버튼
    this.eGui.querySelector('.aptrank-custom-filter-reset').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.eGui.querySelector('input.title-min').value = '';
      this.eGui.querySelector('input.title-max').value = '';
      this.eGui.querySelector('input.min-slider').value = 0;
      this.eGui.querySelector('input.max-slider').value = 1000000;
      updateKoreanLabels();
      updateTrack();
      this.model = null;
      this.params.filterChangedCallback();
    });

    // 닫기 버튼
    const closeBtn = this.eGui.querySelector('.complex-top-filter-panel-close');
    if (closeBtn && this.onClose) {
      closeBtn.addEventListener('click', this.onClose);
    }
  }

  getModel() {
    if (!this.model) return null;
    if (this.model.min === null && this.model.max === null) return null;
    return this.model;
  }

  setModel(model) {
    this.model = model || null;
    if (this.eGui) this.render();
  }

  isFilterActive() {
    return this.model !== null && (this.model.min !== null || this.model.max !== null);
  }

  doesFilterPass() {
    // AG-Grid에서 사용하지 않음 (서버 필터링)
    return true;
  }
}

export default PriceRangeFilter;
